import React, { useState, useRef } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import api, { BACKEND_URL } from "../../utils/api";
import { Download, Upload, FileJson, AlertCircle, CheckCircle, Filter, Users, Heart, Trash2, Music2, Search, Loader2, ExternalLink, Copy } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1997 }, (_, i) => String(CURRENT_YEAR - i));
const TYPES = ["Single", "EP", "Album", "Compilation", "CD"];

export default function ImportExportPage() {
  const { user } = useAuth();
  const isDeveloper = user?.role === "developer";
  // Export filters
  const [exportYear, setExportYear] = useState("");
  const [exportTyp, setExportTyp] = useState("");
  const [exportLabel, setExportLabel] = useState("");
  const [exportLimit, setExportLimit] = useState("");

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importYear, setImportYear] = useState("");
  const [importTyp, setImportTyp] = useState("");
  const [importLimit, setImportLimit] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (exportYear) params.set("year", exportYear);
    if (exportTyp) params.set("typ", exportTyp);
    if (exportLabel) params.set("label", exportLabel);
    if (exportLimit) params.set("limit", exportLimit);
    try {
      const res = await api.get(`/releases/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `releases_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Export fehlgeschlagen"); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setResult(null);
    setError("");
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (Array.isArray(data)) {
        setPreview({ count: data.length, sample: data.slice(0, 3) });
      } else {
        setError("Ungültiges Format: JSON-Array erwartet");
      }
    } catch { setError("Datei konnte nicht geparst werden"); }
  };

  const handleImport = async () => {
    if (!importFile || !preview) return;
    setImporting(true);
    setResult(null);
    setError("");
    try {
      const text = await importFile.text();
      let releases = JSON.parse(text);

      // Client-side pre-filter
      if (importYear) releases = releases.filter(r => {
        const date = r.erscheinungsdatum || "";
        const year = date.includes(".") ? date.split(".").pop()?.trim() : date.slice(0, 4);
        return year === importYear;
      });
      if (importTyp) releases = releases.filter(r => (r.typ || "").toLowerCase() === importTyp.toLowerCase());
      if (importLimit) releases = releases.slice(0, parseInt(importLimit));

      const res = await api.post("/releases/import", {
        releases,
        skip_duplicates: skipDuplicates,
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Import fehlgeschlagen");
    } finally { setImporting(false); }
  };

  // Backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);

  const handleParseArtists = async () => {
    if (!window.confirm("artists_parsed-Feld bei allen Releases neu berechnen? (Kann einige Sekunden dauern)")) return;
    setParsing(true);
    setParseResult(null);
    try {
      const res = await api.post("/releases/parse-artists");
      setParseResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Parsen");
    } finally { setParsing(false); }
  };

  const handleBackfill = async () => {
    if (!window.confirm("Artist-Einträge für alle bestehenden Releases generieren? Das kann einen Moment dauern.")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await api.post("/artists/backfill");
      setBackfillResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Backfill");
    } finally { setBackfilling(false); }
  };

  const handleCleanup = async () => {
    if (!window.confirm("Alle Artists und Labels ohne Releases löschen? Diese Aktion kann nicht rückgängig gemacht werden.")) return;
    setCleaning(true);
    setCleanResult(null);
    try {
      const res = await api.post("/releases/cleanup-orphans");
      setCleanResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Bereinigen");
    } finally { setCleaning(false); }
  };

  const selectCls = "bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-500 transition-colors";
  const inputCls = "bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-500 transition-colors w-full";

  return (
    <AdminLayout>
      <div className="p-8">
      <h1 className="text-2xl font-black text-white font-heading mb-6">Import / Export</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── EXPORT ── */}
        <div className="bg-zinc-900 border border-white/5 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2 text-white font-bold">
            <Download size={16} className="text-rose-400" /> Releases exportieren
          </div>

          {/* Export filters */}
          <div className="space-y-3">
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1">
              <Filter size={11} /> Filter (optional)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Jahr</label>
                <select value={exportYear} onChange={e => setExportYear(e.target.value)} className={selectCls}>
                  <option value="">Alle Jahre</option>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Typ</label>
                <select value={exportTyp} onChange={e => setExportTyp(e.target.value)} className={selectCls}>
                  <option value="">Alle Typen</option>
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Label (enthält)</label>
                <input value={exportLabel} onChange={e => setExportLabel(e.target.value)} placeholder="z.B. Scream" className={inputCls} />
              </div>
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Max. Anzahl</label>
                <input type="number" value={exportLimit} onChange={e => setExportLimit(e.target.value)} placeholder="Alle" className={inputCls} />
              </div>
            </div>
          </div>

          <button
            onClick={handleExport}
            data-testid="export-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg transition-colors"
          >
            <Download size={16} /> JSON exportieren
          </button>
        </div>

        {/* ── IMPORT ── */}
        <div className="bg-zinc-900 border border-white/5 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2 text-white font-bold">
            <Upload size={16} className="text-rose-400" /> Releases importieren
          </div>

          {/* Import filters */}
          <div className="space-y-3">
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider flex items-center gap-1">
              <Filter size={11} /> Filter (optional)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Nur Jahr</label>
                <select value={importYear} onChange={e => setImportYear(e.target.value)} className={selectCls}>
                  <option value="">Alle</option>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-zinc-500 text-xs block mb-1">Nur Typ</label>
                <select value={importTyp} onChange={e => setImportTyp(e.target.value)} className={selectCls}>
                  <option value="">Alle</option>
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-zinc-500 text-xs block mb-1">Max. Einträge</label>
                <input type="number" value={importLimit} onChange={e => setImportLimit(e.target.value)} placeholder="Alle importieren" className={inputCls} />
              </div>
            </div>
          </div>

          {/* File upload */}
          <div
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              importFile ? "border-rose-600/50 bg-rose-900/5" : "border-zinc-800 hover:border-zinc-600"
            }`}
            data-testid="import-dropzone"
          >
            <FileJson size={24} className="mx-auto mb-2 text-zinc-600" />
            <p className="text-zinc-400 text-sm font-medium">
              {importFile ? importFile.name : "JSON-Datei auswählen"}
            </p>
            {preview && (
              <p className="text-zinc-600 text-xs mt-1">{preview.count.toLocaleString("de")} Releases in Datei</p>
            )}
            <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFileChange} />
          </div>

          {/* Skip duplicates toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={skipDuplicates} onChange={e => setSkipDuplicates(e.target.checked)}
              className="w-4 h-4 accent-rose-600" />
            <span className="text-zinc-400 text-sm">Duplikate überspringen</span>
          </label>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 rounded-lg px-3 py-2 border border-red-800/30">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {result && (
            <div className="flex items-center gap-2 text-green-400 text-sm bg-green-900/20 rounded-lg px-3 py-2 border border-green-800/30">
              <CheckCircle size={14} /> {result.inserted} importiert, {result.skipped} übersprungen
            </div>
          )}

          <button
            onClick={handleImport}
            disabled={!preview || importing}
            data-testid="import-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-colors"
          >
            {importing ? "Importiere…" : <><Upload size={16} /> Import starten</>}
          </button>
        </div>
      </div>

      {/* ── ARTIST TOOLS ── */}
      <div className="mt-6 bg-zinc-900 border border-white/5 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-white font-bold text-sm">
          <Users size={15} className="text-rose-400" /> Artist-Synchronisation
        </div>
        <p className="text-zinc-500 text-xs">Beide Schritte nach einem großen Import oder nach einem Parser-Fix ausführen.</p>

        {/* Step 1: Parse */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-white/5">
          <div>
            <p className="text-white text-sm font-semibold">Schritt 1 – Releases parsen</p>
            <p className="text-zinc-500 text-xs mt-0.5">
              Liest alle Releases und berechnet <code className="text-rose-400">artists_parsed</code> neu – z.B. nach einem Parser-Fix (Fifthychild-Bug o.ä.).
            </p>
            {parseResult && (
              <p className="text-green-400 text-xs mt-1.5 flex items-center gap-1">
                <CheckCircle size={11} /> {parseResult.updated} Releases geparst
              </p>
            )}
          </div>
          <button
            onClick={handleParseArtists}
            disabled={parsing}
            data-testid="parse-artists-btn"
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-white font-semibold rounded-lg transition-colors text-sm border border-white/10"
          >
            {parsing ? "Parst…" : "Releases parsen"}
          </button>
        </div>

        {/* Step 2: Backfill */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-white/5">
          <div>
            <p className="text-white text-sm font-semibold">Schritt 2 – Artists generieren</p>
            <p className="text-zinc-500 text-xs mt-0.5">
              Erstellt Artist-Einträge aus Interpret-Feldern <span className="text-zinc-600">+</span> Tracklist-Einträgen (Remixer & Compilation-Artists die nur in Tracklists vorkommen).
            </p>
            {backfillResult && (
              <p className="text-green-400 text-xs mt-1.5 flex items-center gap-1 flex-wrap">
                <CheckCircle size={11} /> {backfillResult.created} Artists erstellt
                {backfillResult.from_tracklist_created > 0 && (
                  <span className="text-blue-400">(davon {backfillResult.from_tracklist_created} nur aus Tracklists)</span>
                )}
                <span className="text-zinc-600">· {backfillResult.skipped} bereits vorhanden</span>
              </p>
            )}
          </div>
          <button
            onClick={handleBackfill}
            disabled={backfilling}
            data-testid="backfill-artists-btn"
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-bold rounded-lg transition-colors text-sm"
          >
            {backfilling ? "Generiere…" : <><Users size={14} /> Artists generieren</>}
          </button>
        </div>
      </div>
      {/* ── VOTES RESET (Developer only) ── */}
      {isDeveloper && <VotesResetSection />}

      {/* ── ORPHAN CLEANUP ── */}
      <div className="mt-6 bg-zinc-900 border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-2 text-white font-bold text-sm mb-1">
          <Trash2 size={15} className="text-red-400" /> Waisen bereinigen
        </div>
        <p className="text-zinc-500 text-xs mb-5">
          Löscht alle Artists und Labels, die keinem einzigen Release zugeordnet sind (release_count = 0).
        </p>
        <div className="flex items-center gap-4">
          <button
            onClick={handleCleanup}
            disabled={cleaning}
            className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            {cleaning ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {cleaning ? "Bereinige…" : "Artists & Labels ohne Releases löschen"}
          </button>
          {cleanResult && (
            <p className="text-green-400 text-xs flex items-center gap-1">
              <CheckCircle size={11} />
              {cleanResult.deleted_artists} Artists · {cleanResult.deleted_labels} Labels gelöscht
              {(cleanResult.updated_labels > 0 || cleanResult.updated_artists > 0) && (
                <span className="text-zinc-500 ml-1">
                  ({cleanResult.updated_labels} Label-Zählungen korrigiert)
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* ── SPOTIFY BULK FILL ── */}
      <SpotifyFillSection />

      {/* ── DUPLIKATE ── */}
      <DeduplicateSection />
      </div>
    </AdminLayout>
  );
}

function VotesResetSection() {
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);

  const handleClear = async () => {
    if (!window.confirm("Alle Votes unwiderruflich löschen und vote_count auf 0 zurücksetzen?")) return;
    setClearing(true);
    setResult(null);
    try {
      const res = await api.delete("/votes/all");
      setResult(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Zurücksetzen");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="mt-6 bg-zinc-900 border border-red-900/30 rounded-xl p-6">
      <div className="flex items-center gap-2 text-white font-bold text-sm mb-1">
        <Heart size={15} className="text-rose-500" /> Votes zurücksetzen
      </div>
      <p className="text-zinc-500 text-xs mb-4">
        Löscht alle abgegebenen Votes und setzt den Like-Zähler aller Releases auf 0. Diese Aktion kann nicht rückgängig gemacht werden.
      </p>

      {result && (
        <div className="flex items-center gap-2 text-green-400 text-xs bg-green-900/20 rounded-lg px-3 py-2 border border-green-800/30 mb-3">
          <CheckCircle size={12} /> {result.votes_deleted} Votes gelöscht · {result.releases_reset} Releases zurückgesetzt
        </div>
      )}

      <button
        onClick={handleClear}
        disabled={clearing}
        data-testid="clear-votes-btn"
        className="flex items-center gap-2 px-4 py-2 bg-red-900/40 hover:bg-red-800/60 disabled:opacity-40 text-red-300 font-semibold rounded-lg transition-colors text-sm border border-red-800/40"
      >
        <Trash2 size={14} /> {clearing ? "Wird zurückgesetzt…" : "Alle Votes löschen"}
      </button>
    </div>
  );
}


function ConfidenceBadge({ score }) {
  if (score >= 75) return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-900/40 text-green-400 border border-green-700/30">{score}%</span>
  );
  if (score >= 45) return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-400 border border-yellow-700/30">{score}%</span>
  );
  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-900/30 text-red-400 border border-red-800/30">{score}%</span>
  );
}

function SpotifyFillSection() {
  const [limit, setLimit]         = useState(50);
  const [scanning, setScanning]   = useState(false);
  const [applying, setApplying]   = useState(false);
  const [results, setResults]     = useState(null);
  const [selected, setSelected]   = useState({});
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError]         = useState("");

  const total        = results?.length ?? 0;
  const withMatch    = results?.filter(r => r.candidate)?.length ?? 0;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const handleScan = async () => {
    setScanning(true);
    setResults(null);
    setSelected({});
    setApplyResult(null);
    setError("");
    try {
      const res = await api.post(`/spotify/bulk-scan?limit=${limit}`, {}, { timeout: 120000 });
      setResults(res.data);
      // Auto-select high-confidence matches (≥75%)
      const autoSel = {};
      res.data.forEach(r => {
        if (r.candidate && r.confidence >= 75) autoSel[r.release_id] = true;
      });
      setSelected(autoSel);
    } catch (e) {
      setError(e.response?.data?.detail || "Scan fehlgeschlagen");
    } finally {
      setScanning(false);
    }
  };

  const handleApply = async () => {
    const items = results
      .filter(r => selected[r.release_id] && r.candidate?.spotify_url)
      .map(r => ({ release_id: r.release_id, spotify_url: r.candidate.spotify_url }));
    if (!items.length) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await api.post("/spotify/bulk-apply", { items });
      setApplyResult(res.data);
      // Remove applied from results
      const appliedIds = new Set(items.map(i => i.release_id));
      setResults(r => r.filter(x => !appliedIds.has(x.release_id)));
      setSelected({});
    } catch (e) {
      setError(e.response?.data?.detail || "Fehler beim Übernehmen");
    } finally {
      setApplying(false);
    }
  };

  const toggleAll = (val) => {
    if (!results) return;
    const next = {};
    results.forEach(r => { if (r.candidate) next[r.release_id] = val; });
    setSelected(next);
  };

  return (
    <div className="mt-6 bg-zinc-900 border border-white/8 rounded-xl p-6">
      <div className="flex items-center gap-2 text-white font-bold text-sm mb-1">
        <Music2 size={15} className="text-green-400" /> Spotify Links automatisch befüllen
      </div>
      <p className="text-zinc-500 text-xs mb-5">
        Scannt Releases ohne Spotify-Link, sucht automatisch den besten Treffer und zeigt
        einen Konfidenz-Score. Grün (&ge;75%) wird automatisch vorausgewählt.
      </p>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <span className="text-zinc-400 text-xs">Releases scannen:</span>
          {[25, 50, 100, 200].map(n => (
            <button key={n} onClick={() => setLimit(n)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${limit === n ? "bg-green-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}>
              {n}
            </button>
          ))}
        </div>
        <button onClick={handleScan} disabled={scanning}
          data-testid="spotify-bulk-scan-btn"
          className="flex items-center gap-2 px-4 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white font-bold text-sm rounded-lg transition-colors ml-auto">
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {scanning ? `Scanne ${limit} Releases…` : "Scan starten"}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs bg-red-900/20 rounded-lg px-3 py-2 border border-red-800/30 mb-4">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {applyResult && (
        <div className="flex items-center gap-2 text-green-400 text-xs bg-green-900/20 rounded-lg px-3 py-2 border border-green-700/30 mb-4">
          <CheckCircle size={12} /> {applyResult.updated} Spotify-Links gespeichert.
          {applyResult.errors?.length > 0 && ` ${applyResult.errors.length} Fehler.`}
        </div>
      )}

      {results && (
        <>
          {/* Summary bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 bg-zinc-800/50 rounded-lg">
            <div className="flex gap-4 text-xs">
              <span className="text-zinc-400">{total} gescannt</span>
              <span className="text-green-400">{withMatch} Treffer</span>
              <span className="text-zinc-300 font-semibold">{selectedCount} ausgewählt</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggleAll(true)} className="text-xs px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-300 transition-colors">Alle wählen</button>
              <button onClick={() => toggleAll(false)} className="text-xs px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-300 transition-colors">Keine</button>
              <button onClick={handleApply} disabled={applying || selectedCount === 0}
                data-testid="spotify-bulk-apply-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-700 hover:bg-green-600 disabled:opacity-40 text-white text-xs font-bold rounded-lg transition-colors">
                {applying ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                {applying ? "Speichern…" : `${selectedCount} übernehmen`}
              </button>
            </div>
          </div>

          {/* Results table */}
          <div className="space-y-1 max-h-[520px] overflow-y-auto pr-1">
            {results.map(r => (
              <div key={r.release_id}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  selected[r.release_id] ? "bg-green-900/10 border border-green-800/30" : "bg-zinc-800/30 border border-white/4"
                }`}>
                {/* Checkbox */}
                <input type="checkbox"
                  disabled={!r.candidate}
                  checked={!!selected[r.release_id]}
                  onChange={e => setSelected(s => ({ ...s, [r.release_id]: e.target.checked }))}
                  className="w-4 h-4 rounded accent-green-500 flex-shrink-0 cursor-pointer disabled:opacity-30"
                />

                {/* Release info */}
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-200 text-xs font-semibold truncate">
                    {r.release_interpret} – {r.release_titel}
                    {r.release_year && <span className="text-zinc-600 font-normal ml-1">({r.release_year})</span>}
                  </p>
                </div>

                {/* Arrow */}
                <span className="text-zinc-700 text-xs flex-shrink-0">→</span>

                {/* Candidate */}
                <div className="flex-1 min-w-0">
                  {r.candidate ? (
                    <div className="flex items-center gap-2">
                      {r.candidate.cover_url && (
                        <img src={r.candidate.cover_url} alt="" className="w-8 h-8 rounded flex-shrink-0 object-cover" />
                      )}
                      <div className="min-w-0">
                        <p className="text-zinc-300 text-xs font-medium truncate">{r.candidate.artist} – {r.candidate.name}</p>
                        <p className="text-zinc-600 text-xs">{r.candidate.release_date?.slice(0,4)} · {r.candidate.album_type}</p>
                      </div>
                    </div>
                  ) : (
                    <span className="text-zinc-700 text-xs">Kein Treffer</span>
                  )}
                </div>

                {/* Confidence + link */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.candidate && <ConfidenceBadge score={r.confidence} />}
                  {r.candidate?.spotify_url && (
                    <a href={r.candidate.spotify_url} target="_blank" rel="noopener noreferrer"
                      className="text-zinc-600 hover:text-green-400 transition-colors" title="Auf Spotify öffnen">
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DeduplicateSection() {
  const [loading, setLoading]     = useState(false);
  const [groups, setGroups]       = useState(null);
  const [selected, setSelected]   = useState({});   // id → true = delete
  const [applying, setApplying]   = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState("");

  const totalToDelete = Object.values(selected).filter(Boolean).length;

  const handleScan = async () => {
    setLoading(true); setGroups(null); setResult(null); setError(""); setSelected({});
    try {
      const res = await api.get("/releases/duplicates");
      setGroups(res.data);
      // Pre-select recommended deletes (keep=false)
      const pre = {};
      res.data.forEach(g => g.entries.forEach(e => { if (!e.keep) pre[e.id] = true; }));
      setSelected(pre);
    } catch (e) {
      setError(e.response?.data?.detail || "Scan fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    const ids = Object.entries(selected).filter(([,v]) => v).map(([k]) => k);
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} Einträge endgültig löschen?`)) return;
    setApplying(true);
    try {
      const res = await api.post("/releases/deduplicate", { delete_ids: ids });
      setResult(res.data);
      setGroups(null); setSelected({});
    } catch (e) {
      setError(e.response?.data?.detail || "Fehler");
    } finally { setApplying(false); }
  };

  return (
    <div className="mt-6 bg-zinc-900 border border-white/8 rounded-xl p-6">
      <div className="flex items-center gap-2 text-white font-bold text-sm mb-1">
        <Copy size={15} className="text-yellow-400" /> Duplikate bereinigen
      </div>
      <p className="text-zinc-500 text-xs mb-5">
        Findet Releases mit gleichem Interpret + Titel. Der vollständigere Eintrag (mehr Links, Tracklist etc.) wird behalten.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <button onClick={handleScan} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-700 hover:bg-yellow-600 disabled:opacity-50 text-white font-bold text-sm rounded-lg transition-colors">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loading ? "Scanne…" : "Duplikate suchen"}
        </button>
        {groups !== null && totalToDelete > 0 && (
          <button onClick={handleDelete} disabled={applying}
            className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-bold text-sm rounded-lg transition-colors">
            {applying ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {applying ? "Löschen…" : `${totalToDelete} löschen`}
          </button>
        )}
      </div>

      {error && <div className="text-red-400 text-xs bg-red-900/20 rounded-lg px-3 py-2 mb-3 border border-red-800/30">{error}</div>}

      {result && (
        <div className="flex items-center gap-2 text-green-400 text-xs bg-green-900/20 rounded-lg px-3 py-2 mb-3 border border-green-700/30">
          <CheckCircle size={12} /> {result.deleted} Duplikate gelöscht.
        </div>
      )}

      {groups !== null && groups.length === 0 && (
        <div className="text-green-400 text-xs bg-green-900/20 rounded-lg px-3 py-2 border border-green-700/30">
          <CheckCircle size={12} className="inline mr-1" /> Keine Duplikate gefunden – Datenbank ist sauber.
        </div>
      )}

      {groups?.length > 0 && (
        <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
          {groups.map(g => (
            <div key={g.key} className="bg-zinc-800/50 border border-white/5 rounded-xl p-3">
              <p className="text-zinc-300 text-xs font-semibold mb-2 truncate">{g.key} <span className="text-zinc-600">({g.count}x)</span></p>
              <div className="space-y-1.5">
                {g.entries.map(e => (
                  <div key={e.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-xs transition-colors ${
                    selected[e.id]
                      ? "bg-red-900/20 border-red-800/40"
                      : "bg-zinc-900 border-white/5"
                  }`}>
                    <input type="checkbox"
                      checked={!!selected[e.id]}
                      onChange={ev => setSelected(s => ({ ...s, [e.id]: ev.target.checked }))}
                      className="w-3.5 h-3.5 rounded accent-red-500 flex-shrink-0 cursor-pointer"
                    />
                    {/* Keep badge */}
                    {e.keep
                      ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-900/40 text-green-400 border border-green-700/30 flex-shrink-0">BEHALTEN</span>
                      : <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-900/30 text-red-400 border border-red-800/30 flex-shrink-0">LÖSCHEN</span>
                    }
                    {/* Details */}
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <span className="text-zinc-500">{e.date?.slice(0,10) || "–"}</span>
                      <span className={e.has_spotify ? "text-green-500" : "text-zinc-700"}>Spotify</span>
                      <span className={e.has_cover ? "text-blue-400" : "text-zinc-700"}>Cover</span>
                      {e.track_count > 0 && <span className="text-zinc-400">{e.track_count} Tracks</span>}
                      <span className="text-zinc-600 text-[10px] ml-auto font-mono truncate">{e.id.slice(-6)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

